import { useCallback, useRef, useEffect } from 'react';

/**
 * Debounce utility function to limit the rate at which a function can fire
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - The debounced function
 */
export function debounce(func, delay) {
  let timeoutId;
  
  const debounced = function(...args) {
    clearTimeout(timeoutId);
    
    return new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        const result = func.apply(this, args);
        resolve(result);
      }, delay);
    });
  };
  
  // Add cancel method to clear pending invocations
  debounced.cancel = function() {
    clearTimeout(timeoutId);
  };
  
  return debounced;
}

/**
 * Hook version of debounce for React components
 */

export function useDebounce(callback, delay) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef();
  
  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    return new Promise((resolve) => {
      timeoutRef.current = setTimeout(() => {
        const result = callbackRef.current(...args);
        resolve(result);
      }, delay);
    });
  }, [delay]);
  
  return debouncedCallback;
}